import DebugObject from '@/etc/debug.object'
import { v4 as Uuid } from 'uuid'
import { DEFAULT_DURATION } from '@/etc/consts'
import { get as _get, set as _set } from 'lodash'
import {
  getQuickbarSmartCandidates,
  getQuickbarZoomLinkedProps,
  isQuickbarZoomEnvelopeProp,
} from '@/editor/smart/quickbarSmart'

export default class AbstractAsset extends DebugObject {
  constructor(uuid) {
    super()

    this._type = 'abstract'

    this._asset = {
      uuid: uuid || Uuid(),
      mime: null,
      blob: null,
      thumbnail: null,
      ready: false,
      meta: {},
      color: this.color(),
    }

    this._props = {}
    this._lastProps = {}
  }

  get uuid() {
    return this._asset.uuid
  }
  set uuid(next) {}
  get type() {
    return this._type
  }
  set type(next) {}
  isType(type) {
    const isParent = typeof super.isType === 'function'
      ? super.isType(type)
      : false
    return this.type === type || isParent
  }
  isGlobalType(type) {
    const isParent = typeof super.isGlobalType === 'function'
      ? super.isGlobalType(type)
      : false
    return this.type.startsWith(type) || isParent
  }

  get blob() {
    return this._asset.blob
  }
  set blob(next) {}

  get thumbnail() {
    return this._asset.thumbnail
  }
  set thumbnail(next) {}

  get meta() {
    return this._asset.meta
  }
  set meta(next) {}

  get defaultProps() {
    return this._props
  }
  set defaultProps(next) {}

  get lastProps() {
    return this._lastProps
  }
  set lastProps(next) {}

  get D() {
    return 0
  }
  set D(next) {}

  get resizable() {
    return false
  }
  set resizable(next) {}

  get PROPS() {
    return {}
  }
  set PROPS(next) {}

  _propsObject(obj) {
    const list = []
    Object.keys(obj).forEach((code) => {
      if (obj[code].type !== 'object') {
        list.push(code)
        return
      }
      const childs = this._propsObject(obj[code].fields)
      childs.forEach((c) => {
        list.push(c)
      })
    })
    return list
  }

  props() {
    return this._propsObject(this.PROPS)
  }

  _flatProp(prop, path, group = '') {
    const ret = []
    ret.push({
      code: path,
      group,
      depth: path.split('.').length,
      type: prop.type,
      name: prop.name,
      tool: prop.tool || null,
      options: prop.options || null,
      min: typeof prop.min === 'number' ? prop.min : null,
      max: typeof prop.max === 'number' ? prop.max : null,
      step: typeof prop.step === 'number' ? prop.step : null,
      normalized: !!prop.normalized,
      slider: !!prop.slider,
    })
    if (prop.type === 'object') {
      Object.keys(prop.fields).forEach((code) => {
        const list = this._flatProp(prop.fields[code], `${path}.${code}`, path)
        ret.push(list)
      })
    }
    return ret
  }

  get FPROPS() {
    const props = this.PROPS
    const ret = []
    Object.keys(props).forEach((code) => {
      const list = this._flatProp(props[code], code)
      ret.push(list)
    })
    const flat = ret.flat(Infinity)
    const groupTools = flat.reduce((acc, el) => {
      if (el.type === 'object' && el.tool) {
        acc[el.code] = el.tool
      }
      return acc
    }, {})
    return flat.map((el) => {
      if (el.group && !el.tool && groupTools[el.group])
        return {
          ...el,
          tool: groupTools[el.group],
        }
      return el
    })
  }
  set FPROPS(next) {}

  ptype(code) {
    const p = _get(this.PROPS, code)
    if (!p) return 'unknown'
    return p.type
  }

  pget(code) {
    return _get(this._props, code)
  }

  pset(code, val) {
    _set(this._props, code, val)
  }

  toObject(withBinary = false) {
    const ret = {
      uuid: this.uuid,
      type: this.type,
      // mime: this._asset.mime,
      meta: { ...this._asset.meta },
      blob: withBinary ? this._asset.blob.slice() : null,
      thumbnail: withBinary ? this._asset.thumbnail.slice() : null,
    }
    return ret
  }
  fromObject(obj) {
    this._uuid = obj.uuid
    this._asset.mime = obj.mime
    this._asset.meta = obj.meta
    if (obj.blob) this._asset.blob = new Blob([obj.blob], { type: obj.mime })
    if (obj.thumbnail)
      this._asset.thumbnail = new Blob([obj.thumbnail], { type: 'image/png' })
  }
  _loadFromFile(file) {
    return new Promise((resolve) => {
      this._asset.blob = new Blob([file], { type: file.type })
      this._asset.mime = file.mime || file.type
      resolve()
    })
  }

  loadFromFile(file) {
    return this._loadFromFile(file)
  }

  KF(kf) {
    return {
      uuid: Uuid(),
      time: 0,
      type: 'point',
      delay: {},
      props: {},
      easing: {},
      ...(kf || {}),
    }
  }

  initAnimation(projectSettings) {
    this.dbg.log(
      'should be updated/reimplemented by child classes!',
      projectSettings
    )
    const animation = {
      uuid: Uuid(),
      keyframes: [],
    }
    // if (this.D < 2) {
    // animation.keyframes.push(this.KF())
    // } else {
    const length = this.meta.duration || DEFAULT_DURATION
    try {
      const v2 = !!require('@/store').default.state.project.settings.legacyKeyframesV2
      if (v2) {
        // In V2, do not seed default keys; leave the list empty so values remain static until keyed
        animation.keyframes = []
      } else {
        const entry = this.KF({ type: 'entry', time: 0, removable: false })
        const exit = this.KF({ type: 'exit', time: length, removable: false })
        animation.keyframes = [entry, exit]
      }
    } catch (e) {
      const entry = this.KF({ type: 'entry', time: 0, removable: false })
      const exit = this.KF({ type: 'exit', time: length, removable: false })
      animation.keyframes = [entry, exit]
    }
    // }
    return animation
  }

  updateFrame(time, localTime, frame) {
    this.dbg.log('update frame abstract', { time, localTime, frame })
    if (!frame.animation || localTime < 0 || frame.strip.length < localTime) {
      return {}
    }
    // Phase 1 (timeline-domain keyframes): Evaluate animation in clip-local timeline time.
    // This matches traditional editors: changing clip speed/length compresses/expands keyframe timing.
    const timeScale = 1
    const evalTime = localTime

    // Phase 4: Smart Key Group evaluator (outer loop only) takes precedence when enabled
    try {
      const settings = require('@/store').default.state.project.settings
      // Text strips use SmartKeys unconditionally (numeric-only envelope) to avoid enabling
      // SmartKeys globally (which impacts video behavior).
      const isTextStrip = !!(frame && frame.strip && frame.strip.asset && frame.strip.asset.type === 'text')
      const fullSmartOn = !!settings.smartKeyGroupsEnabled || isTextStrip
      // Phase 1: effective scope for beat-locked micro/impact loops ('global' | 'local'); default to global
      const beatScope = settings.smartBeatLockScope || 'global'
      const smart = (frame.strip && frame.strip.smartKeys) ? frame.strip.smartKeys : null
      const assetForQuickbarSmart = {
        type: this.type,
        defaultProps: this.defaultProps || this._props || {},
      }
      const quickbarSmartCandidates = getQuickbarSmartCandidates(assetForQuickbarSmart)
      const quickbarSmartProps = new Set()
      if (!fullSmartOn && smart && quickbarSmartCandidates.length) {
        quickbarSmartCandidates.forEach((candidate) => {
          if (candidate === 'opacity') {
            if (smart.opacity) quickbarSmartProps.add('opacity')
            return
          }
          if (isQuickbarZoomEnvelopeProp(candidate)) {
            getQuickbarZoomLinkedProps(candidate).forEach((prop) => {
              if (smart[prop]) quickbarSmartProps.add(prop)
            })
          }
        })
      }
      const clipQuickbarSmartOn = !fullSmartOn && quickbarSmartProps.size > 0
      const smartOn = fullSmartOn || clipQuickbarSmartOn
      if (smartOn && smart && Object.keys(smart).length) {
        const baseAnimatedProps = frame.animation?.propsAt(evalTime) || {}
        // Helpers
        const secFromUnit = (u, val, settings) => {
          if (!Number.isFinite(val)) return 0
          if (u === 'seconds' || !u) return val
          if (u === 'frames') return val / (settings.fps || 30)
          if (u === 'beats') {
            try {
              const { makeBeatCode } = require('@/etc/beatcode')
              const bc = makeBeatCode(settings)
              return val * bc.secondsPerQuarter()
            } catch (e) {
              return val * (60 / (settings.bpm || 120))
            }
          }
          return val
        }

        // Combine smart values per property; basic outer interpolation across Entry/Sustain/Exit
        const resolved = {}
        const interp = (a, b, t, easingKey) => {
          const k = easingKey || 'Linear.None'
          try {
            const { EASING_FN } = require('@/etc/consts')
            const easeFn = EASING_FN[k] || EASING_FN['Linear.None']
            const tt = typeof easeFn === 'function' ? easeFn(t) : t
            return a + (b - a) * tt
          } catch (e) {
            return a + (b - a) * t
          }
        }

        // Phase 2 (matrix): handle 'transform' group first; it writes position/rotation/scale
        if (fullSmartOn && smart.transform) {
          const grp = smart.transform || {}
          const entryOff = grp?.offsets?.entry || 0
          const exitOff = grp?.offsets?.exit || 0
          const entryTime = 0 + entryOff
          const exitTime = Math.max(0, (frame.strip.length * timeScale) - exitOff)
          const leftV = grp.left || { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, scale: { x: 1, y: 1, z: 1 } }
          const rightV = (grp.links && grp.links.rightToLeft) ? leftV : (grp.right || leftV)
          const startV = grp.start || leftV
          const endV = grp.end || rightV
          const evalMatrix = () => {
            let tpos = 0
            if (evalTime <= entryTime) {
              tpos = entryTime > 0 ? (evalTime / entryTime) : 1
              const eKeyEntry = grp?.entryEasing || 'Linear.None'
              return {
                position: {
                  x: interp(startV.position.x, leftV.position.x, tpos, eKeyEntry),
                  y: interp(startV.position.y, leftV.position.y, tpos, eKeyEntry),
                  z: interp(startV.position.z, leftV.position.z, tpos, eKeyEntry),
                },
                rotation: {
                  x: interp(startV.rotation.x, leftV.rotation.x, tpos, eKeyEntry),
                  y: interp(startV.rotation.y, leftV.rotation.y, tpos, eKeyEntry),
                  z: interp(startV.rotation.z, leftV.rotation.z, tpos, eKeyEntry),
                },
                scale: {
                  x: interp(startV.scale.x, leftV.scale.x, tpos, eKeyEntry),
                  y: interp(startV.scale.y, leftV.scale.y, tpos, eKeyEntry),
                  z: interp(startV.scale.z, leftV.scale.z, tpos, eKeyEntry),
                }
              }
            }
            if (evalTime >= exitTime) {
              const span = Math.max(0.0001, (frame.strip.length * timeScale))
              tpos = Math.min(1, (evalTime - exitTime) / (span - exitTime))
              const eKeyExit = grp?.exitEasing || 'Linear.None'
              return {
                position: {
                  x: interp(rightV.position.x, endV.position.x, tpos, eKeyExit),
                  y: interp(rightV.position.y, endV.position.y, tpos, eKeyExit),
                  z: interp(rightV.position.z, endV.position.z, tpos, eKeyExit),
                },
                rotation: {
                  x: interp(rightV.rotation.x, endV.rotation.x, tpos, eKeyExit),
                  y: interp(rightV.rotation.y, endV.rotation.y, tpos, eKeyExit),
                  z: interp(rightV.rotation.z, endV.rotation.z, tpos, eKeyExit),
                },
                scale: {
                  x: interp(rightV.scale.x, endV.scale.x, tpos, eKeyExit),
                  y: interp(rightV.scale.y, endV.scale.y, tpos, eKeyExit),
                  z: interp(rightV.scale.z, endV.scale.z, tpos, eKeyExit),
                }
              }
            }
            // Sustain
            const span = Math.max(0.0001, (exitTime - entryTime))
            tpos = Math.min(1, Math.max(0, (evalTime - entryTime) / span))
            const easingKey = grp?.sustainEasing || 'Linear.None'
            return {
              position: {
                x: interp(leftV.position.x, rightV.position.x, tpos, easingKey),
                y: interp(leftV.position.y, rightV.position.y, tpos, easingKey),
                z: interp(leftV.position.z, rightV.position.z, tpos, easingKey),
              },
              rotation: {
                x: interp(leftV.rotation.x, rightV.rotation.x, tpos, easingKey),
                y: interp(leftV.rotation.y, rightV.rotation.y, tpos, easingKey),
                z: interp(leftV.rotation.z, rightV.rotation.z, tpos, easingKey),
              },
              scale: {
                x: interp(leftV.scale.x, rightV.scale.x, tpos, easingKey),
                y: interp(leftV.scale.y, rightV.scale.y, tpos, easingKey),
                z: interp(leftV.scale.z, rightV.scale.z, tpos, easingKey),
              }
            }
          }
          let mv = evalMatrix()

          // Phase 5 (transform): apply group-level micro/impact per axis if present
          const tMicro = grp?.micro
          if (tMicro) {
            const settings = require('@/store').default.state.project.settings
            const beatLocked = !!tMicro.beatLocked
            const effScope = tMicro.beatLockScope || beatScope
            // region bounds (in the same domain as transform eval)
            const region = tMicro.region || 'sustain'
            let baseTime = evalTime
            let regionStart = entryTime
            let regionEnd = exitTime
            if (beatLocked) {
              if (effScope === 'global') {
                const gStart = frame.strip.start
                const gEntryEnd = gStart + entryOff
                const gExitStart = gStart + frame.strip.length - exitOff
                const gEnd = gStart + frame.strip.length
                baseTime = time
                regionStart = region === 'entry' ? gStart : (region === 'exit' ? gExitStart : gEntryEnd)
                regionEnd = region === 'entry' ? gEntryEnd : (region === 'exit' ? gEnd : gExitStart)
              } else {
                const scaleForBeat = (timeScale || 1)
                const entryBase = (entryTime / scaleForBeat)
                const exitBase = (exitTime / scaleForBeat)
                baseTime = localTime
                regionStart = region === 'entry' ? 0 : (region === 'exit' ? exitBase : entryBase)
                regionEnd = region === 'entry' ? entryBase : (region === 'exit' ? (frame.strip.length * timeScale) / scaleForBeat : exitBase)
              }
            }

            const D = Math.max(0, regionEnd - regionStart)
            const useInstances = !!tMicro.instancesEnabled
            const n = Math.max(1, Math.floor(Number.isFinite(tMicro.instances) ? tMicro.instances : 1))
            const duty = Math.max(0, Math.min(1, Number.isFinite(tMicro.duty) ? tMicro.duty : 1))
            const amp = Number.isFinite(tMicro.amp) ? tMicro.amp : 0.2
            const shape = tMicro.shape || 'parent'

            let everySec = secFromUnit(tMicro.every?.unit, tMicro.every?.value || 0, settings)
            let spanSec = secFromUnit(tMicro.span?.unit, tMicro.span?.value || 0, settings) || everySec
            if (useInstances && D > 0) {
              everySec = D / n
              spanSec = Math.max(1e-6, everySec * duty)
            }

            const applyAxis = (axisPath, leftVal, startVal, rightVal, endVal, current) => {
              if (!(everySec > 0 && baseTime >= regionStart && baseTime <= regionEnd)) return current
              const startAligned = beatLocked && !useInstances
                ? Math.ceil(regionStart / everySec) * everySec
                : regionStart
              const rel = Math.max(0, baseTime - startAligned)
              let idx = Math.floor(rel / everySec)
              if (useInstances) idx = Math.max(0, Math.min(n - 1, Math.floor((baseTime - regionStart) / everySec)))
              let instanceStart = (useInstances ? regionStart : startAligned) + idx * everySec
              if (useInstances && beatLocked) {
                try {
                  const { makeBeatCode } = require('@/etc/beatcode')
                  const bc = makeBeatCode(settings)
                  const unit = tMicro.gridUnit || 'quarter'
                  const grid = unit === 'frame'
                    ? 1 / (settings.fps || 30)
                    : unit === 'quarter' ? bc.secondsPerQuarter()
                    : unit === 'eighth' ? bc.secondsFor('eighth')
                    : unit === 'half' ? bc.secondsFor('half')
                    : unit === 'whole' ? bc.secondsFor('whole')
                    : unit === 'sixteenth' ? bc.secondsFor('sixteenth')
                    : bc.secondsPerQuarter()
                  const q = Math.round(instanceStart / grid) * grid
                  instanceStart = Math.max(regionStart, Math.min(regionEnd, q))
                  if (instanceStart + spanSec > regionEnd) instanceStart = Math.max(regionStart, regionEnd - spanSec)
                } catch (e) { void e }
              }
              const within = baseTime - instanceStart
              if (within < 0 || within > spanSec) return current

              if (shape === 'parent') {
                let easingKey = tMicro.easing || (
                  region === 'entry' ? (grp?.entryEasing || 'Linear.None') :
                  region === 'exit' ? (grp?.exitEasing || 'Linear.None') :
                  (grp?.sustainEasing || 'Linear.None')
                )
                let ease = null
                try {
                  const { EASING_FN } = require('@/etc/consts')
                  ease = EASING_FN[easingKey] || EASING_FN['Linear.None']
                } catch (e) { void e }
                const u = Math.min(1, Math.max(0, within / Math.max(1e-6, spanSec)))
                const m = typeof ease === 'function' ? ease(u) : u
                const regionDelta = region === 'entry' ? (leftVal - startVal) : (region === 'exit' ? (endVal - rightVal) : (rightVal - leftVal))
                if (axisPath.startsWith('scale.')) return current * (1 + amp * m)
                return current + amp * m * regionDelta
              } else {
                // custom pattern for transform: treat pattern value as additive/multiplicative delta
                const mEntry = secFromUnit(tMicro.pattern?.offsets?.entry?.unit, tMicro.pattern?.offsets?.entry?.value || 0, settings)
                const mExit = secFromUnit(tMicro.pattern?.offsets?.exit?.unit, tMicro.pattern?.offsets?.exit?.value || 0, settings)
                const mEntryT = 0 + mEntry
                const mExitT = Math.max(0, spanSec - mExit)
                const mLeft = typeof tMicro.pattern?.left?.value !== 'undefined' ? tMicro.pattern.left.value : 0
                const mRight = typeof tMicro.pattern?.right?.value !== 'undefined' ? tMicro.pattern.right.value : 0
                const mStart = typeof tMicro.pattern?.start?.value !== 'undefined' ? tMicro.pattern.start.value : mLeft
                const mEnd = typeof tMicro.pattern?.end?.value !== 'undefined' ? tMicro.pattern.end.value : mRight
                let mvv = mLeft
                if (within <= mEntryT) {
                  const p2 = mEntryT > 0 ? (within / mEntryT) : 1
                  mvv = mStart + (mLeft - mStart) * p2
                } else if (within >= mExitT) {
                  const span2 = Math.max(0.0001, spanSec)
                  const p2 = Math.min(1, (within - mExitT) / (span2 - mExitT))
                  mvv = mRight + (mEnd - mRight) * p2
                } else {
                  mvv = mLeft
                }
                if (axisPath.startsWith('scale.')) return current * (Number.isFinite(mvv) ? mvv : 1)
                return current + (Number.isFinite(mvv) ? mvv : 0)
              }
            }

            // apply per-axis
            mv.position.x = applyAxis('position.x', leftV.position.x, startV.position.x, rightV.position.x, endV.position.x, mv.position.x)
            mv.position.y = applyAxis('position.y', leftV.position.y, startV.position.y, rightV.position.y, endV.position.y, mv.position.y)
            mv.position.z = applyAxis('position.z', leftV.position.z, startV.position.z, rightV.position.z, endV.position.z, mv.position.z)
            mv.rotation.x = applyAxis('rotation.x', leftV.rotation.x, startV.rotation.x, rightV.rotation.x, endV.rotation.x, mv.rotation.x)
            mv.rotation.y = applyAxis('rotation.y', leftV.rotation.y, startV.rotation.y, rightV.rotation.y, endV.rotation.y, mv.rotation.y)
            mv.rotation.z = applyAxis('rotation.z', leftV.rotation.z, startV.rotation.z, rightV.rotation.z, endV.rotation.z, mv.rotation.z)
            mv.scale.x = applyAxis('scale.x', leftV.scale.x, startV.scale.x, rightV.scale.x, endV.scale.x, mv.scale.x)
            mv.scale.y = applyAxis('scale.y', leftV.scale.y, startV.scale.y, rightV.scale.y, endV.scale.y, mv.scale.y)
            mv.scale.z = applyAxis('scale.z', leftV.scale.z, startV.scale.z, rightV.scale.z, endV.scale.z, mv.scale.z)

            // impact for transform (parent-shaped only)
            if (shape === 'parent' && tMicro.impact) {
              const impact = tMicro.impact
              const iSpan = secFromUnit(impact.span?.unit, impact.span?.value || 0, settings) || ((regionEnd - regionStart) / 12)
              const placement = impact.placement || 'center'
              const iOffset = secFromUnit(impact.offset?.unit, impact.offset?.value || 0, settings)
              const ampI = Number.isFinite(impact.amp) ? impact.amp : 0.2
              const sustainStart = 0
              const sustainEnd = Math.max(1e-6, spanSec)
              const sustainMid = sustainStart + (sustainEnd - sustainStart) / 2
              // reuse instanceStart computation
              const startAligned = beatLocked && !useInstances ? Math.ceil(regionStart / everySec) * everySec : regionStart
              const rel = Math.max(0, baseTime - startAligned)
              let idx = Math.floor(rel / everySec)
              if (useInstances) idx = Math.max(0, Math.min(n - 1, Math.floor((baseTime - regionStart) / everySec)))
              let instanceStart = (useInstances ? regionStart : startAligned) + idx * everySec
              if (useInstances && beatLocked) {
                try {
                  const { makeBeatCode } = require('@/etc/beatcode')
                  const bc = makeBeatCode(settings)
                  const unit = tMicro.gridUnit || 'quarter'
                  const grid = unit === 'frame' ? 1 / (settings.fps || 30)
                    : unit === 'quarter' ? bc.secondsPerQuarter()
                    : unit === 'eighth' ? bc.secondsFor('eighth')
                    : unit === 'half' ? bc.secondsFor('half')
                    : unit === 'whole' ? bc.secondsFor('whole')
                    : unit === 'sixteenth' ? bc.secondsFor('sixteenth')
                    : bc.secondsPerQuarter()
                  const q = Math.round(instanceStart / grid) * grid
                  instanceStart = Math.max(regionStart, Math.min(regionEnd, q))
                  if (instanceStart + spanSec > regionEnd) instanceStart = Math.max(regionStart, regionEnd - spanSec)
                } catch (e) { void e }
              }
              const within = baseTime - instanceStart
              let iStart = sustainMid - iSpan / 2
              if (placement === 'custom') iStart = sustainStart + iOffset
              if (iStart < sustainStart) iStart = sustainStart
              if (iStart + iSpan > sustainEnd) iStart = Math.max(sustainStart, sustainEnd - iSpan)
              const wi = within - iStart
              if (wi >= 0 && wi <= iSpan) {
                const iEntry = secFromUnit(impact.pattern?.offsets?.entry?.unit, impact.pattern?.offsets?.entry?.value || 0, settings)
                const iExit = secFromUnit(impact.pattern?.offsets?.exit?.unit, impact.pattern?.offsets?.exit?.value || 0, settings)
                const iEntryT = 0 + iEntry
                const iExitT = Math.max(0, iSpan - iExit)
                const iLeft = typeof impact.pattern?.left?.value !== 'undefined' ? impact.pattern.left.value : 0
                const iRight = typeof impact.pattern?.right?.value !== 'undefined' ? impact.pattern.right.value : 0
                const iStartV = typeof impact.pattern?.start?.value !== 'undefined' ? impact.pattern.start.value : iLeft
                const iEndV = typeof impact.pattern?.end?.value !== 'undefined' ? impact.pattern.end.value : iRight
                let iv = iLeft
                if (wi <= iEntryT) {
                  const p3 = iEntryT > 0 ? (wi / iEntryT) : 1
                  iv = iStartV + (iLeft - iStartV) * p3
                } else if (wi >= iExitT) {
                  const span3 = Math.max(0.0001, iSpan)
                  const p3 = Math.min(1, (wi - iExitT) / (span3 - iExitT))
                  iv = iRight + (iEndV - iRight) * p3
                } else {
                  iv = iLeft
                }
                mv.position.x += ampI * (Number.isFinite(iv) ? iv : 0)
                mv.position.y += ampI * (Number.isFinite(iv) ? iv : 0)
                mv.position.z += ampI * (Number.isFinite(iv) ? iv : 0)
                mv.rotation.x += ampI * (Number.isFinite(iv) ? iv : 0)
                mv.rotation.y += ampI * (Number.isFinite(iv) ? iv : 0)
                mv.rotation.z += ampI * (Number.isFinite(iv) ? iv : 0)
                mv.scale.x *= (1 + ampI * ((Number.isFinite(iv) ? iv : 1) - 1))
                mv.scale.y *= (1 + ampI * ((Number.isFinite(iv) ? iv : 1) - 1))
                mv.scale.z *= (1 + ampI * ((Number.isFinite(iv) ? iv : 1) - 1))
              }
            }
          }

          resolved['position.x'] = mv.position.x
          resolved['position.y'] = mv.position.y
          resolved['position.z'] = mv.position.z
          resolved['rotation.x'] = mv.rotation.x
          resolved['rotation.y'] = mv.rotation.y
          resolved['rotation.z'] = mv.rotation.z
          resolved['scale.x'] = mv.scale.x
          resolved['scale.y'] = mv.scale.y
          resolved['scale.z'] = mv.scale.z
        }
        const _transformAxes = ['position.x','position.y','position.z','rotation.x','rotation.y','rotation.z','scale.x','scale.y','scale.z']
        const _textSmartAxes = new Set([..._transformAxes, 'opacity'])
        Object.keys(smart).forEach(prop => {
          if (prop === 'transform') return
          if (fullSmartOn && smart.transform && _transformAxes.includes(prop)) return
          if (clipQuickbarSmartOn && !fullSmartOn && !quickbarSmartProps.has(prop)) return
          if (isTextStrip && !_textSmartAxes.has(prop)) return
          const grp = smart[prop] || {}
          const entryOff = grp?.offsets?.entry || 0
          const exitOff = grp?.offsets?.exit || 0
          const entryTime = 0 + entryOff
          const exitTime = Math.max(0, (frame.strip.length * timeScale) - exitOff)
          const leftVal = typeof grp?.left?.value !== 'undefined' ? grp.left.value : this._props[prop]
          const linkRL = !!(grp?.links && grp.links.rightToLeft)
          const rightVal = linkRL
            ? leftVal
            : (typeof grp?.right?.value !== 'undefined' ? grp.right.value : this._props[prop])
          const startVal = typeof grp?.start?.value !== 'undefined' ? grp.start.value : leftVal
          const endVal = typeof grp?.end?.value !== 'undefined' ? grp.end.value : rightVal
          let base = leftVal
          if (evalTime <= entryTime) {
            const p = entryTime > 0 ? (evalTime / entryTime) : 1
            const easingKey = grp?.entryEasing || 'Linear.None'
            try {
              const { EASING_FN } = require('@/etc/consts')
              const easeFn = EASING_FN[easingKey] || EASING_FN['Linear.None']
              const eased = typeof easeFn === 'function' ? easeFn(p) : p
              base = startVal + (leftVal - startVal) * eased
            } catch (e) {
              base = startVal + (leftVal - startVal) * p
            }
          } else if (evalTime >= exitTime) {
            const span = Math.max(0.0001, (frame.strip.length * timeScale))
            const p = Math.min(1, (evalTime - exitTime) / (span - exitTime))
            const easingKey = grp?.exitEasing || 'Linear.None'
            try {
              const { EASING_FN } = require('@/etc/consts')
              const easeFn = EASING_FN[easingKey] || EASING_FN['Linear.None']
              const eased = typeof easeFn === 'function' ? easeFn(p) : p
              base = rightVal + (endVal - rightVal) * eased
            } catch (e) {
              base = rightVal + (endVal - rightVal) * p
            }
          } else {
            // Sustain: interpolate left -> right using optional easing
            const span = Math.max(0.0001, (exitTime - entryTime))
            const tpos = Math.min(1, Math.max(0, (evalTime - entryTime) / span))
            const easingKey = grp?.sustainEasing || 'Linear.None'
            try {
              const { EASING_FN } = require('@/etc/consts')
              const easeFn = EASING_FN[easingKey] || EASING_FN['Linear.None']
              const eased = typeof easeFn === 'function' ? easeFn(tpos) : tpos
              base = leftVal + (rightVal - leftVal) * eased
            } catch (e) {
              base = leftVal + (rightVal - leftVal) * tpos
            }
          }

          // Phase 5: Micro loop inside region(s)
          // Text strips use per-region micro (`microByRegion`) like EffectOverlay.
          // Fall back to legacy `micro` when present.
          let micro = grp?.micro || null
          if (!micro && grp?.microByRegion && typeof grp.microByRegion === 'object') {
            // Choose region based on envelope phase.
            const regionKey = evalTime <= entryTime ? 'entry' : (evalTime >= exitTime ? 'exit' : 'sustain')
            micro = grp.microByRegion[regionKey] || grp.microByRegion.sustain || null
            if (micro && typeof micro === 'object' && !micro.region) micro = { ...micro, region: regionKey }
          }

          if (micro) {
            const region = micro.region || 'sustain'
            const settings = require('@/store').default.state.project.settings
            // Effects-style rate: rateUnit + dotted + triplet -> beats.
            const beatsForRate = (m) => {
              const unit = (m && m.rateUnit) ? String(m.rateUnit) : null
              const base = unit === 'sixteenth'
                ? 0.25
                : unit === 'eighth'
                  ? 0.5
                  : unit === 'half'
                    ? 2
                    : unit === 'whole'
                      ? 4
                      : 1 // quarter default
              const dotted = (m && m.dotted) ? 3 / 2 : 1
              const triplet = (m && m.triplet) ? 2 / 3 : 1
              return base * dotted * triplet
            }

            // Default to existing every/span, but if rateUnit is provided we derive beats like EffectOverlay.
            let everySecRaw = secFromUnit(micro.every?.unit, micro.every?.value || 0, settings)
            let spanSecRaw = secFromUnit(micro.span?.unit, micro.span?.value || 0, settings) || everySecRaw
            if (micro && (micro.rateUnit || micro.dotted || micro.triplet)) {
              const beats = beatsForRate(micro)
              everySecRaw = secFromUnit('beats', beats, settings)
              spanSecRaw = everySecRaw
            }
            const beatLocked = !!micro.beatLocked

            // Determine base-time domain and region bounds per scope
            const effScope = micro.beatLockScope || beatScope
            let baseTime = evalTime
            let regionStart = entryTime
            let regionEnd = exitTime
            if (beatLocked) {
              if (effScope === 'global') {
                // Global cadence: use project time for cadence and global region bounds
                const gStart = frame.strip.start
                const gEntryEnd = gStart + entryOff
                const gExitStart = gStart + frame.strip.length - exitOff
                const gEnd = gStart + frame.strip.length
                baseTime = time
                regionStart = region === 'entry' ? gStart : (region === 'exit' ? gExitStart : gEntryEnd)
                regionEnd = region === 'entry' ? gEntryEnd : (region === 'exit' ? gEnd : gExitStart)
              } else {
                // Local cadence (legacy behavior): use visual time and compensate by timeScale
                const scaleForBeat = (timeScale || 1)
                const entryBase = (entryTime / scaleForBeat)
                const exitBase = (exitTime / scaleForBeat)
                baseTime = localTime
                regionStart = region === 'entry' ? 0 : (region === 'exit' ? exitBase : entryBase)
                regionEnd = region === 'entry' ? entryBase : (region === 'exit' ? (frame.strip.length * timeScale) / scaleForBeat : exitBase)
              }
            } else {
              // Non beat-locked: operate in clip domain
              baseTime = evalTime
              regionStart = entryTime
              regionEnd = exitTime
            }

            // Phase (beats): shift time domain just like EffectOverlay tween phase.
            const phaseBeats = Number.isFinite(Number(micro.phaseBeats)) ? Number(micro.phaseBeats) : 0
            if (phaseBeats !== 0) {
              const phaseSec = secFromUnit('beats', phaseBeats, settings)
              baseTime = baseTime - phaseSec
            }

            const D = Math.max(0, regionEnd - regionStart)
            const useInstances = !!micro.instancesEnabled
            const n = Math.max(1, Math.floor(Number.isFinite(micro.instances) ? micro.instances : 1))
            const duty = Math.max(0, Math.min(1, Number.isFinite(micro.duty) ? micro.duty : 1))
            const amp = Number.isFinite(micro.amp) ? micro.amp : 0.2
            const shape = micro.shape || 'parent'
            let everySec = everySecRaw
            let spanSec = spanSecRaw
            if (useInstances && D > 0) {
              everySec = D / n
              spanSec = Math.max(1e-6, everySec * duty)
            }

            if (everySec > 0 && baseTime >= regionStart && baseTime <= regionEnd) {
              // choose instance index
              const startAligned = beatLocked && !useInstances
                ? Math.ceil(regionStart / everySec) * everySec
                : regionStart

              const rel = Math.max(0, baseTime - startAligned)
              let idx = Math.floor(rel / everySec)
              if (useInstances) {
                idx = Math.max(0, Math.min(n - 1, Math.floor((baseTime - regionStart) / everySec)))
              }

              // instance start (with optional beat-quantization when instances enabled)
              let instanceStart = (useInstances ? regionStart : startAligned) + idx * everySec
              if (useInstances && beatLocked) {
                try {
                  const { makeBeatCode } = require('@/etc/beatcode')
                  const bc = makeBeatCode(settings)
                  const unit = micro.gridUnit || 'quarter'
                  const grid = unit === 'frame'
                    ? 1 / (settings.fps || 30)
                    : unit === 'quarter' ? bc.secondsPerQuarter()
                    : unit === 'eighth' ? bc.secondsFor('eighth')
                    : unit === 'half' ? bc.secondsFor('half')
                    : unit === 'whole' ? bc.secondsFor('whole')
                    : unit === 'sixteenth' ? bc.secondsFor('sixteenth')
                    : bc.secondsPerQuarter()
                  const q = Math.round(instanceStart / grid) * grid
                  // clamp inside region; ensure end within region
                  instanceStart = Math.max(regionStart, Math.min(regionEnd, q))
                  if (instanceStart + spanSec > regionEnd) instanceStart = Math.max(regionStart, regionEnd - spanSec)
                } catch (e) { void e }
              }

              // try current and neighbor instances to handle quantization rounding edge-cases
              const tryStarts = [instanceStart]
              if (useInstances) {
                const prev = instanceStart - everySec
                const next = instanceStart + everySec
                tryStarts.push(prev, next)
              }

              for (let s of tryStarts) {
                const within = baseTime - s
                if (within < 0 || within > spanSec) continue

                if (shape === 'parent') {
                  // parent-shaped modulation using region easing
                  let easingKey = micro.easing || (
                    region === 'entry' ? (grp?.entryEasing || 'Linear.None') :
                    region === 'exit' ? (grp?.exitEasing || 'Linear.None') :
                    (grp?.sustainEasing || 'Linear.None')
                  )
                  let ease = null
                  try {
                    const { EASING_FN } = require('@/etc/consts')
                    ease = EASING_FN[easingKey] || EASING_FN['Linear.None']
                  } catch (e) { void e }
                  const u = Math.min(1, Math.max(0, within / Math.max(1e-6, spanSec)))
                  const m = typeof ease === 'function' ? ease(u) : u

                  // compute region delta for additive composition
                  const regionDelta = region === 'entry'
                    ? (leftVal - startVal)
                    : region === 'exit'
                      ? (endVal - rightVal)
                      : (rightVal - leftVal)

                  if (prop.includes('scale') || prop === 'opacity') {
                    base = base * (1 + amp * m)
                  } else if (prop.startsWith('position.')) {
                    base = base + amp * m * regionDelta
                  } else {
                    base = base + amp * m * regionDelta
                  }

                } else if (shape === 'windowRelative') {
                  // Relative window: oscillate around base using explicit window low/high offsets.
                  // - If `micro.window.low/high` exist, use them (supports unlinking).
                  // - Otherwise fall back to symmetric `micro.radius`.
                  const ampWin = Number.isFinite(Number(micro.amp)) ? Number(micro.amp) : 1
                  const win = micro.window || {}
                  let low = Number.isFinite(Number(win.low)) ? Number(win.low) : null
                  let high = Number.isFinite(Number(win.high)) ? Number(win.high) : null
                  if (!(Number.isFinite(low) && Number.isFinite(high))) {
                    const radius = Number.isFinite(Number(micro.radius)) ? Number(micro.radius) : 0
                    if (Math.abs(radius) > 1e-6) {
                      low = -radius
                      high = radius
                    }
                  }

                  if (Number.isFinite(low) && Number.isFinite(high) && high !== low) {
                    let easingKey = micro.easing || (
                      region === 'entry' ? (grp?.entryEasing || 'Linear.None') :
                      region === 'exit' ? (grp?.exitEasing || 'Linear.None') :
                      (grp?.sustainEasing || 'Linear.None')
                    )
                    let ease = null
                    try {
                      const { EASING_FN } = require('@/etc/consts')
                      ease = EASING_FN[easingKey] || EASING_FN['Linear.None']
                    } catch (e) { void e }
                    const u = Math.min(1, Math.max(0, within / Math.max(1e-6, spanSec)))
                    const yoyo = (typeof micro.yoyo === 'boolean') ? micro.yoyo : true
                    let m = 0
                    if (yoyo) {
                      if (u <= 0.5) m = typeof ease === 'function' ? ease(u * 2) : interp(0, 1, u * 2, easingKey)
                      else m = typeof ease === 'function' ? ease((1 - u) * 2) : interp(1, 0, (u - 0.5) * 2, easingKey)
                    } else {
                      m = typeof ease === 'function' ? ease(u) : interp(0, 1, u, easingKey)
                    }
                    // Lerp low->high (and back when yoyo). Treat low/high as offsets.
                    const lo = low * ampWin
                    const hi = high * ampWin
                    const offset = lo + (hi - lo) * m
                    if (prop && prop.startsWith('scale.')) {
                      // Scale windowRelative is multiplicative around base via (1 + offset).
                      // Clamp so it can "disappear" (0.01) but not invert.
                      const factor = Math.max(0.01, Math.min(2, 1 + offset))
                      base = base * factor
                    } else if (prop === 'opacity') {
                      base = Math.max(0, Math.min(1, base + offset))
                    } else {
                      base = base + offset
                    }
                  }
                } else {
                  // shape === 'custom' → legacy pattern behavior
                const mEntry = secFromUnit(micro.pattern?.offsets?.entry?.unit, micro.pattern?.offsets?.entry?.value || 0, settings)
                const mExit = secFromUnit(micro.pattern?.offsets?.exit?.unit, micro.pattern?.offsets?.exit?.value || 0, settings)
                const mEntryT = 0 + mEntry
                const mExitT = Math.max(0, spanSec - mExit)
                const mLeft = typeof micro.pattern?.left?.value !== 'undefined' ? micro.pattern.left.value : 0
                const mRight = typeof micro.pattern?.right?.value !== 'undefined' ? micro.pattern.right.value : 0
                const mStart = typeof micro.pattern?.start?.value !== 'undefined' ? micro.pattern.start.value : mLeft
                const mEnd = typeof micro.pattern?.end?.value !== 'undefined' ? micro.pattern.end.value : mRight
                let mv = mLeft
                if (within <= mEntryT) {
                  const p2 = mEntryT > 0 ? (within / mEntryT) : 1
                  mv = mStart + (mLeft - mStart) * p2
                } else if (within >= mExitT) {
                  const span2 = Math.max(0.0001, spanSec)
                  const p2 = Math.min(1, (within - mExitT) / (span2 - mExitT))
                  mv = mRight + (mEnd - mRight) * p2
                } else {
                  mv = mLeft
                }

                // Impact loop inside micro sustain (Phase 6)
                const impact = micro.impact
                if (impact) {
                  const iSpan = secFromUnit(impact.span?.unit, impact.span?.value || 0, settings) || (mExitT - mEntryT) / 4
                  const placement = impact.placement || 'center'
                  const iOffset = secFromUnit(impact.offset?.unit, impact.offset?.value || 0, settings)
                  const sustainStart = mEntryT
                  const sustainEnd = mExitT
                    const sustainMid = sustainStart + (sustainEnd - sustainStart) / 2
                    let iStart = sustainMid - iSpan / 2
                    if (placement === 'custom') iStart = sustainStart + iOffset
                    if (iStart < sustainStart) iStart = sustainStart
                    if (iStart + iSpan > sustainEnd) iStart = Math.max(sustainStart, sustainEnd - iSpan)
                    const wi = within - iStart
                    if (wi >= 0 && wi <= iSpan) {
                      const iEntry = secFromUnit(impact.pattern?.offsets?.entry?.unit, impact.pattern?.offsets?.entry?.value || 0, settings)
                      const iExit = secFromUnit(impact.pattern?.offsets?.exit?.unit, impact.pattern?.offsets?.exit?.value || 0, settings)
                      const iEntryT = 0 + iEntry
                      const iExitT = Math.max(0, iSpan - iExit)
                      const iLeft = typeof impact.pattern?.left?.value !== 'undefined' ? impact.pattern.left.value : 0
                      const iRight = typeof impact.pattern?.right?.value !== 'undefined' ? impact.pattern.right.value : 0
                      const iStartV = typeof impact.pattern?.start?.value !== 'undefined' ? impact.pattern.start.value : iLeft
                      const iEndV = typeof impact.pattern?.end?.value !== 'undefined' ? impact.pattern.end.value : iRight
                      let iv = iLeft
                      if (wi <= iEntryT) {
                        const p3 = iEntryT > 0 ? (wi / iEntryT) : 1
                        iv = iStartV + (iLeft - iStartV) * p3
                      } else if (wi >= iExitT) {
                        const span3 = Math.max(0.0001, iSpan)
                        const p3 = Math.min(1, (wi - iExitT) / (span3 - iExitT))
                        iv = iRight + (iEndV - iRight) * p3
                      } else {
                        iv = iLeft
                      }
                      const impactAmp = Number.isFinite(impact.amp) ? impact.amp : null
                      if (prop.includes('scale') || prop === 'opacity') {
                        base = impactAmp != null ? base * (1 + impactAmp * ((Number.isFinite(iv) ? iv : 1) - 1)) : base * (Number.isFinite(iv) ? iv : 1)
                      } else if (prop.startsWith('position.')) {
                        base = impactAmp != null ? base + impactAmp * (Number.isFinite(iv) ? iv : 0) : base + (Number.isFinite(iv) ? iv : 0)
                      } else {
                        base = impactAmp != null ? base + impactAmp * (Number.isFinite(iv) ? iv : 0) : base + (Number.isFinite(iv) ? iv : 0)
                      }
                    }
                  }

                  // Compose micro over base (legacy custom pattern)
                  if (prop.includes('scale') || prop === 'opacity') {
                    base = base * (Number.isFinite(mv) ? mv : 1)
                  } else if (prop.startsWith('position.')) {
                    base = base + (Number.isFinite(mv) ? mv : 0)
                  } else {
                    base = base + (Number.isFinite(mv) ? mv : 0)
                  }
                }

                // Impact for parent-shaped mode (optional), placed inside full instance span
                if (shape === 'parent' && micro.impact) {
                  const impact = micro.impact
                  const iSpan = secFromUnit(impact.span?.unit, impact.span?.value || 0, settings) || (spanSec / 4)
                  const placement = impact.placement || 'center'
                  const iOffset = secFromUnit(impact.offset?.unit, impact.offset?.value || 0, settings)
                  const sustainStart = 0
                  const sustainEnd = spanSec
                  const sustainMid = sustainStart + (sustainEnd - sustainStart) / 2
                  let iStart = sustainMid - iSpan / 2
                  if (placement === 'custom') iStart = sustainStart + iOffset
                  if (iStart < sustainStart) iStart = sustainStart
                  if (iStart + iSpan > sustainEnd) iStart = Math.max(sustainStart, sustainEnd - iSpan)
                  const wi = within - iStart
                  if (wi >= 0 && wi <= iSpan) {
                    const iEntry = secFromUnit(impact.pattern?.offsets?.entry?.unit, impact.pattern?.offsets?.entry?.value || 0, settings)
                    const iExit = secFromUnit(impact.pattern?.offsets?.exit?.unit, impact.pattern?.offsets?.exit?.value || 0, settings)
                    const iEntryT = 0 + iEntry
                    const iExitT = Math.max(0, iSpan - iExit)
                    const iLeft = typeof impact.pattern?.left?.value !== 'undefined' ? impact.pattern.left.value : 0
                    const iRight = typeof impact.pattern?.right?.value !== 'undefined' ? impact.pattern.right.value : 0
                    const iStartV = typeof impact.pattern?.start?.value !== 'undefined' ? impact.pattern.start.value : iLeft
                    const iEndV = typeof impact.pattern?.end?.value !== 'undefined' ? impact.pattern.end.value : iRight
                    let iv = iLeft
                    if (wi <= iEntryT) {
                      const p3 = iEntryT > 0 ? (wi / iEntryT) : 1
                      iv = iStartV + (iLeft - iStartV) * p3
                    } else if (wi >= iExitT) {
                      const span3 = Math.max(0.0001, iSpan)
                      const p3 = Math.min(1, (wi - iExitT) / (span3 - iExitT))
                      iv = iRight + (iEndV - iRight) * p3
                    } else {
                      iv = iLeft
                    }
                    const impactAmp = Number.isFinite(impact.amp) ? impact.amp : 0.2
                    if (prop.includes('scale') || prop === 'opacity') {
                      base = base * (1 + impactAmp * ((Number.isFinite(iv) ? iv : 1) - 1))
                    } else if (prop.startsWith('position.')) {
                      base = base + impactAmp * (Number.isFinite(iv) ? iv : 0)
                    } else {
                      base = base + impactAmp * (Number.isFinite(iv) ? iv : 0)
                    }
                  }
                }

                break
              }
              // no application when outside any instance → no-op
            }
          }

          resolved[prop] = base
        })
        // Phase 9: basic sanity checks in dev mode
        if (settings.devValidationEnabled) {
          Object.keys(resolved).forEach(k => {
            const v = resolved[k]
            if (!Number.isFinite(v)) console.warn('SmartKeys resolved non-finite', { prop: k, v })
          })
        }
        const finalProps = {
          ...baseAnimatedProps,
          ...resolved,
        }
        this.applyProps(finalProps)
        return finalProps
      }
    } catch (err) {
      // ignore smart group errors; fall back to keyframes
    }

    const ret = frame.animation.propsAt(evalTime);
    this.applyProps(ret);
    this.dbg.log('update frame abstract apply props', ret);
    return ret;

  }

  applyProps(last) {
    this.dbg.log('apply prop', last)
    this._lastProps = {
      ...this._props,
      ...this._lastProps,
      ...last,
    }
  }

  toolUpdate(update) {
    this.dbg.log('tool update', update)
    return {}
  }

  color() {
    return '#314245'
  }

  icon() {
    return []
  }
}
